/**
 * Padrão visual dos passos: cada tipo pertence a uma família, e a família
 * dá a cor (faixa e ícone do card, painel e paleta) e o grupo da paleta.
 * Assim o operador entende o passo só de olhar.
 *
 * Cores em `tokens.css` (`--fam-*`, com versão para o tema escuro).
 */
import type { NodeKind } from '../types';

export type NodeFamily =
  | 'start'
  | 'decide'
  | 'send'
  | 'wait'
  | 'deadline'
  | 'mark'
  | 'notify';

export interface FamilyMeta {
  label: string;
  accent: string;
  accentBg: string;
}

export const FAMILY_META: Record<NodeFamily, FamilyMeta> = {
  start: {
    label: 'Início',
    accent: 'var(--fam-start)',
    accentBg: 'var(--fam-start-bg)',
  },
  decide: {
    label: 'Decidir',
    accent: 'var(--fam-decide)',
    accentBg: 'var(--fam-decide-bg)',
  },
  send: {
    label: 'Enviar',
    accent: 'var(--fam-send)',
    accentBg: 'var(--fam-send-bg)',
  },
  wait: {
    label: 'Esperar',
    accent: 'var(--fam-wait)',
    accentBg: 'var(--fam-wait-bg)',
  },
  deadline: {
    label: 'Prazo e risco',
    accent: 'var(--fam-deadline)',
    accentBg: 'var(--fam-deadline-bg)',
  },
  mark: {
    label: 'Marcar e atualizar',
    accent: 'var(--fam-mark)',
    accentBg: 'var(--fam-mark-bg)',
  },
  notify: {
    label: 'Avisar e integrar',
    accent: 'var(--fam-notify)',
    accentBg: 'var(--fam-notify-bg)',
  },
};

/** Ordem dos grupos na paleta. */
export const FAMILY_ORDER: NodeFamily[] = [
  'start',
  'decide',
  'send',
  'wait',
  'deadline',
  'mark',
  'notify',
];

export const NODE_FAMILY: Record<NodeKind, NodeFamily> = {
  trigger: 'start',
  switch: 'decide',
  disp: 'send',
  offer: 'send',
  cancel: 'send',
  'smart-disp': 'send',
  check: 'wait',
  track: 'wait',
  event: 'wait',
  delay: 'wait',
  'promised-date': 'deadline',
  'delay-risk': 'deadline',
  'apply-tag': 'mark',
  'update-fields': 'mark',
  'http-request': 'notify',
  notify: 'notify',
  'create-dispute': 'notify',
};

export const familyOf = (kind: string): FamilyMeta =>
  FAMILY_META[NODE_FAMILY[kind as NodeKind] ?? 'notify'];

/**
 * O front sabe desenhar e editar este tipo (tem card e formulário). Tipo
 * do catálogo que não passa aqui aparece como "Em breve".
 */
export const isKnownNodeKind = (kind: string): kind is NodeKind =>
  Object.prototype.hasOwnProperty.call(NODE_FAMILY, kind);

/** Automação de hoje que o passo substitui (linha "Substitui: …" no painel). */
export const NODE_REPLACES: Partial<Record<NodeKind, string>> = {
  disp: 'Automações de envio',
  offer: 'Automações de ofertas',
  'delay-risk': 'Tiers de risco de atraso',
  'promised-date': 'Automação de prazo prometido',
  event: 'Automações de reenvio',
  check: 'Automações por inatividade',
  track: 'Automações por inatividade e de reenvio',
  'apply-tag': 'Automações de marcadores',
};
